import { Injectable, OnDestroy, effect, inject, signal } from '@angular/core';
import { MonitorConfiguration, PhoenixService } from '@approot/services';
import { sortBy } from 'lodash';
import { Channel } from 'phoenix';
import { ago } from '../shared';

export type Message = {
  parent: string;
  name: string;
  label: string;
  max_age_lo: number;
  max_age_hi: number;
  diff: number;
  alertLevel?: number;
  ago?: string;
  updated?: number;
  timeSinceupdate?: number;
  timeSinceupdateStr?: string;
  dynamicDiff?: number;
};

const ChannelPrefix = 'room:marketdata:';

@Injectable({ providedIn: 'root' })
export class MarketDataService implements OnDestroy {
  public readonly messages = signal<Message[]>([]);
  private interval: any;
  private readonly phxService = inject(PhoenixService);
  private readonly connected = this.phxService.connected;
  private channels: Channel[] = [];
  private config: MonitorConfiguration;

  public constructor() {
    effect(async () => {
      if (this.connected()) {
        this.reconnect();
      }
    });
  }

  // Probably not needed as we never "route away" from the top-level component
  public ngOnDestroy(): void {
    console.log('Destroying market data service');
    this.channels.forEach(c => c.leave());
    clearInterval(this.interval);
  }

  private async reconnect() {
    await this.phxService.waitForPhx();
    this.channels.forEach(c => c.leave());
    this.channels = [];
    this.init();
  }

  private init() {
    this.phxService.joinChannel('room:config', {
      params: {
        filter: 'market_data_sources'
      },
      onJoin: async (resp: any) => {
        this.config = resp.config;
        const channelList = await this.getChannels();
        this.channels = channelList.map(c => {
          const channel = this.phxService.joinChannel(c);
          channel.on('shout', this.onMessage.bind(this));
          return channel;
        });
        this.interval = setInterval(() => this.updateTimes(), 1000);
      }
    });
  }

  private updateTimes() {
    this.messages.update(message => {
      return message.map(m => {
        m.timeSinceupdate = Math.round((Date.now() - m.updated) / 1000);
        m.timeSinceupdateStr = ago(m.timeSinceupdate);
        m.dynamicDiff = m.diff + m.timeSinceupdate;
        m.ago = m.dynamicDiff < 400_000_000 ? ago(m.dynamicDiff) : `>10 years`;
        m.alertLevel = this.getMarketDataAlertLevel(m);
        return m;
      });
    });
  }

  private async getChannels() {
    const channels = this.config?.market_data_sources.parents || [];
    return channels.map(c => `${ChannelPrefix}${c}`);
  }

  private onMessage(payload: Message) {
    this.messages.update(message => {
      message = message.filter(m => m.parent !== payload.parent);
      message.push({
        ...payload,
        ago: payload.diff < 400_000_000 ? ago(payload.diff) : `>10 years`,
        updated: Date.now(),
        timeSinceupdate: 0,
        timeSinceupdateStr: '0s',
        dynamicDiff: payload.diff,
        alertLevel: this.getMarketDataAlertLevel(payload)
      });
      return sortBy(message, ['parent']);
    });
  }

  public getMarketDataAlertLevel(payload: Message): number {
    const { dynamicDiff, diff, max_age_lo, max_age_hi } = payload;
    const d = dynamicDiff ?? diff;
    if (d < max_age_lo) {
      return 0;
    }
    if (d < max_age_hi) {
      return 0.5;
    }
    return 1.0;
  }
}
